import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  DataTable,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  HairlineCard,
  Icons,
  iconSize,
  MenuItem,
  palette,
  RowAction,
  RowActions,
  Stack,
  TagChip,
  TextField,
  titleCellSx,
  Typography,
  useRowActionsColumn,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import type { SeatEventSummary, SeatHolder } from '@munifocus/contracts-gov';
import { useCompanies } from '../../lib/administration-api';
import {
  useChangeSeat,
  useCompanySeats,
  useSeatHistory,
  type SeatChange,
} from '../../lib/seats-api';
import { useScope } from '../../providers/ScopeProvider';
import { useMutationToast } from '../../lib/mutation-toast';
import { useAuth } from '../../providers/AuthProvider';
import { frenchFirstOfMonth } from '@munifocus/utils';

/** "3 of 5 seats" — the headline the spec asks the company page to show. */
export function SeatCount({ used, limit }: { used: number; limit: number }) {
  const { t } = useTranslation();
  return (
    <Typography component="span" sx={{ fontWeight: 700, color: palette.text.primary }}>
      {t('gov.admin.seats.count', { used, limit })}
    </Typography>
  );
}

/**
 * A private enforcement company's named enforcement seats.
 *
 * Only a seat holder can sign in to the enforcement app; everyone else stays on
 * the staff list for free. The administrator gives seats out, takes them back
 * (which signs the person out at once) and moves them between employees, as
 * often as the work needs — and can do all of it without holding a seat.
 */
export function SeatsPanel() {
  const { t, i18n } = useTranslation();
  const toast = useMutationToast();
  const actionsColumn = useRowActionsColumn();
  const { active, can } = useScope();
  const { user } = useAuth();
  const companies = useCompanies(active?.kind !== 'company');
  const companyId = active?.kind === 'company' ? active.id : (companies.data?.[0]?.id ?? null);

  const seats = useCompanySeats(companyId);
  const history = useSeatHistory(companyId);
  const change = useChangeSeat(companyId ?? '');
  const canManage = can('employee:manage');

  const [assigning, setAssigning] = useState(false);
  const [moving, setMoving] = useState<SeatHolder | null>(null);
  const [removing, setRemoving] = useState<SeatHolder | null>(null);
  const [target, setTarget] = useState('');

  const run = async (successKey: string, body: SeatChange): Promise<boolean> => {
    try {
      await change.mutateAsync(body);
      toast.success(successKey);
      return true;
    } catch (err) {
      toast.error(err, 'gov.admin.seats.failed');
      return false;
    }
  };

  if (!companyId) {
    return (
      <HairlineCard sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {t('gov.admin.employees.noCompany')}
        </Typography>
      </HairlineCard>
    );
  }

  const data = seats.data;
  const full = data ? data.used >= data.limit : false;
  const french = i18n.language?.startsWith('fr');
  const self = (row: SeatHolder) => row.userId === user?.id;

  const closeDialogs = () => {
    setAssigning(false);
    setMoving(null);
    setTarget('');
  };

  return (
    <Stack spacing={2}>
      <HairlineCard sx={{ p: 2.5 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
        >
          <Box>
            <Typography variant="h3" sx={{ fontSize: 20, mb: 0.5 }}>
              {data ? <SeatCount used={data.used} limit={data.limit} /> : '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('gov.admin.seats.explainer')}
            </Typography>
            {data && (
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                {t('gov.admin.seats.breakdown', {
                  plan: data.planSeats,
                  extra: data.extraSeats,
                  complimentary: data.complimentarySeats,
                })}
                {data.plans.length > 0 &&
                  ` · ${data.plans.map((p) => (french ? p.planNameFr : p.planNameEn)).join(', ')}`}
              </Typography>
            )}
          </Box>
          {canManage && (
            <Button
              variant="contained"
              startIcon={<Icons.UserPlus size={iconSize.md} />}
              disabled={!data || data.unseated.length === 0}
              onClick={() => setAssigning(true)}
            >
              {t('gov.admin.seats.assign')}
            </Button>
          )}
        </Stack>
        {full && canManage && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('gov.admin.seats.fullHint')}
          </Alert>
        )}
      </HairlineCard>

      <DataTable
        rows={data?.holders ?? []}
        getRowKey={(row) => row.membershipId}
        isLoading={seats.isLoading}
        emptyLabel={t('gov.admin.seats.emptyHolders')}
        initialSortKey="name"
        columns={[
          {
            key: 'name',
            label: t('gov.admin.seats.holder'),
            primary: true,
            sortValue: (row) => row.userName,
            cell: (row) => (
              <Stack>
                <Typography variant="body2" sx={titleCellSx}>
                  {row.userName}
                  {self(row) ? ` · ${t('gov.admin.seats.you')}` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.userEmail}
                </Typography>
              </Stack>
            ),
          },
          {
            key: 'role',
            label: t('gov.admin.employees.role'),
            sortValue: (row) => row.role,
            cell: (row) => <TagChip label={t(`gov.admin.roles.${row.role}`)} />,
          },
          {
            key: 'since',
            label: t('gov.admin.seats.since'),
            nowrap: true,
            sortValue: (row) => row.seatAssignedAt ?? '',
            cell: (row) =>
              row.seatAssignedAt
                ? frenchFirstOfMonth(new Date(row.seatAssignedAt).toLocaleDateString(i18n.language))
                : '—',
          },
          ...(canManage
            ? [
                {
                  ...actionsColumn,
                  cell: (row: SeatHolder) => (
                    <RowActions>
                      <RowAction
                        label={t('gov.admin.seats.move')}
                        icon={Icons.ArrowRightLeft}
                        disabled={(data?.unseated.length ?? 0) === 0}
                        onClick={() => setMoving(row)}
                      />
                      <RowAction
                        label={t('gov.admin.seats.remove')}
                        icon={Icons.UserMinus}
                        tone="danger"
                        onClick={() => setRemoving(row)}
                      />
                    </RowActions>
                  ),
                },
              ]
            : []),
        ]}
      />

      <HairlineCard sx={{ p: 2.5 }}>
        <Typography variant="h3" sx={{ fontSize: 16, mb: 1 }}>
          {t('gov.admin.seats.historyTitle')}
        </Typography>
        <SeatHistory rows={history.data ?? []} loading={history.isLoading} />
      </HairlineCard>

      {/* Assign or move: pick the employee the seat goes to. */}
      <Dialog open={assigning || Boolean(moving)} onClose={closeDialogs} fullWidth maxWidth="sm">
        <DialogTitle>
          {moving
            ? t('gov.admin.seats.moveTitle', { name: moving.userName })
            : t('gov.admin.seats.assignTitle')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {!moving && full && (
              <Alert severity="warning">{t('gov.errors.code.no_seat_available')}</Alert>
            )}
            <TextField
              select
              fullWidth
              label={t('gov.admin.seats.employee')}
              value={target}
              onChange={(e) => setTarget(e.target.value)}
            >
              {(data?.unseated ?? []).map((row) => (
                <MenuItem key={row.membershipId} value={row.membershipId}>
                  {row.userName} · {t(`gov.admin.roles.${row.role}`)}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" color="text.secondary">
              {moving ? t('gov.admin.seats.moveHelp') : t('gov.admin.seats.assignHelp')}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialogs}>{t('common.actions.cancel')}</Button>
          <Button
            variant="contained"
            disabled={!target || change.isPending}
            onClick={async () => {
              const ok = moving
                ? await run('gov.admin.seats.moved', {
                    action: 'move',
                    fromMembershipId: moving.membershipId,
                    toMembershipId: target,
                  })
                : await run('gov.admin.seats.assigned', { action: 'assign', membershipId: target });
              if (ok) closeDialogs();
            }}
          >
            {moving ? t('gov.admin.seats.move') : t('gov.admin.seats.assign')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove: says plainly that it signs the person out. */}
      <Dialog open={Boolean(removing)} onClose={() => setRemoving(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          {t('gov.admin.seats.removeTitle', { name: removing?.userName ?? '' })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {removing && self(removing)
              ? t('gov.admin.seats.removeSelfBody')
              : t('gov.admin.seats.removeBody')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(null)}>{t('common.actions.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            disabled={change.isPending}
            onClick={async () => {
              if (!removing) return;
              const ok = await run('gov.admin.seats.removed', {
                action: 'remove',
                membershipId: removing.membershipId,
              });
              if (ok) setRemoving(null);
            }}
          >
            {t('gov.admin.seats.remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

/** Who moved which seat, when, from whom and to whom. */
function SeatHistory({ rows, loading }: { rows: SeatEventSummary[]; loading: boolean }) {
  const { t, i18n } = useTranslation();
  return (
    <DataTable
      rows={rows}
      getRowKey={(row) => row.id}
      isLoading={loading}
      emptyLabel={t('gov.admin.seats.historyEmpty')}
      pageSize={10}
      initialSortKey="when"
      columns={[
        {
          key: 'when',
          label: t('gov.admin.seats.when'),
          nowrap: true,
          sortValue: (row) => row.createdAt,
          cell: (row) => frenchFirstOfMonth(new Date(row.createdAt).toLocaleString(i18n.language)),
        },
        {
          key: 'action',
          label: t('gov.admin.seats.action'),
          sortValue: (row) => row.action,
          cell: (row) => <TagChip label={t(`gov.admin.seats.actions.${row.action}`)} />,
        },
        {
          key: 'from',
          label: t('gov.admin.seats.from'),
          sortValue: (row) => row.fromUserName ?? '',
          cell: (row) => row.fromUserName ?? '—',
        },
        {
          key: 'to',
          label: t('gov.admin.seats.to'),
          sortValue: (row) => row.toUserName ?? '',
          cell: (row) => row.toUserName ?? '—',
        },
        {
          key: 'by',
          label: t('gov.admin.seats.by'),
          sortValue: (row) => row.actorName ?? '',
          cell: (row) =>
            row.actorName ??
            (row.actorPortal === 'system'
              ? t('gov.admin.seats.bySystem')
              : t('gov.admin.seats.byMunifocus')),
        },
        {
          key: 'count',
          label: t('gov.admin.seats.after'),
          nowrap: true,
          sortValue: (row) => row.seatsUsed,
          cell: (row) => t('gov.admin.seats.count', { used: row.seatsUsed, limit: row.seatLimit }),
        },
      ]}
    />
  );
}
